/**
 * plateCoeffs.ts - Współczynniki tablicowe dla obliczeń płyt
 * PlexiSystem Engineering Module
 * Źródło: Tablice inżynierskie, Roark's Formulas for Stress and Strain
 */

export type BoundaryCondition = 'fixed' | 'simply_supported' | 'mixed';
export type LoadType = 'uniform' | 'hydrostatic' | 'point_center' | 'point_edge';

interface PlateCoefficients {
  Csig: number;  // Współczynnik naprężeń
  Cw: number;    // Współczynnik ugięcia
}

interface CoeffTable {
  aOverB: number;
  Csig: number;
  Cw: number;
}

// Tabele współczynników dla płyt prostokątnych
const COEFFS_FIXED: CoeffTable[] = [
  { aOverB: 1.0, Csig: 0.0478, Cw: 0.00406 },
  { aOverB: 1.5, Csig: 0.0505, Cw: 0.00540 },
  { aOverB: 2.0, Csig: 0.0560, Cw: 0.00690 },
  { aOverB: 3.0, Csig: 0.0670, Cw: 0.01020 },
  { aOverB: 4.0, Csig: 0.0790, Cw: 0.01350 },
];

const COEFFS_SIMPLY_SUPPORTED: CoeffTable[] = [
  { aOverB: 1.0, Csig: 0.0796, Cw: 0.0116 },
  { aOverB: 1.5, Csig: 0.0920, Cw: 0.0152 },
  { aOverB: 2.0, Csig: 0.1120, Cw: 0.0205 },
  { aOverB: 3.0, Csig: 0.1350, Cw: 0.0290 },
  { aOverB: 4.0, Csig: 0.1600, Cw: 0.0380 },
];

/**
 * Interpoluje współczynniki dla dowolnego stosunku boków
 */
export function interpolateCoefficients(
  aOverB: number,
  boundary: BoundaryCondition
): PlateCoefficients {
  // Wybierz odpowiednią tabelę
  const table = boundary === 'fixed' ? COEFFS_FIXED : COEFFS_SIMPLY_SUPPORTED;
  
  // Jeśli poza zakresem → przyjmij wartości dla końców
  if (aOverB <= table[0].aOverB) {
    return { Csig: table[0].Csig, Cw: table[0].Cw };
  }
  if (aOverB >= table[table.length - 1].aOverB) {
    return { 
      Csig: table[table.length - 1].Csig, 
      Cw: table[table.length - 1].Cw 
    };
  }
  
  // Szukamy przedziału do interpolacji
  for (let i = 0; i < table.length - 1; i++) {
    const left = table[i];
    const right = table[i + 1];
    
    if (aOverB >= left.aOverB && aOverB <= right.aOverB) {
      const ratio = (aOverB - left.aOverB) / (right.aOverB - left.aOverB);
      const Csig = left.Csig + (right.Csig - left.Csig) * ratio;
      const Cw = left.Cw + (right.Cw - left.Cw) * ratio;
      return { Csig, Cw };
    }
  }
  
  // Fallback (nie powinno się zdarzyć)
  return { Csig: table[0].Csig, Cw: table[0].Cw };
}

/**
 * Oblicza współczynnik korekcyjny dla grubości płyty
 */
export function getThicknessCorrection(thickness_mm: number, span_mm: number): number {
  const ratio = thickness_mm / span_mm;
  
  // Dla cienkich płyt (t/L < 0.05) - teoria cienkich płyt
  if (ratio < 0.05) return 1.0;
  
  // Dla grubych płyt - korekcja Mindlina
  // Wzór przybliżony: k = 1 + 0.6 * (t/L)^2
  return 1 + 0.6 * Math.pow(ratio, 2);
}

/**
 * Zwraca zalecany współczynnik bezpieczeństwa
 */
export function getSafetyFactor(
  loadType: 'static' | 'dynamic' | 'impact' | 'hydrostatic',
  material: 'brittle' | 'ductile',
  longTerm: boolean = false
): number {
  const factors = {
    static: { brittle: 2.5, ductile: 2.0 },
    dynamic: { brittle: 3.5, ductile: 2.5 },
    impact: { brittle: 5.0, ductile: 3.0 },
    hydrostatic: { brittle: 3.0, ductile: 2.5 }
  };
  
  let factor = factors[loadType][material];
  
  // Redukcja dla obciążeń długotrwałych (pełzanie)
  if (longTerm) {
    factor *= 1.5; // Zwiększ współczynnik bezpieczeństwa
  }
  
  return factor;
}

/**
 * Sprawdza czy płyta wymaga żebrowania
 */
export function checkRibbingRequired(
  a_mm: number,
  t_mm: number,
  H_mm: number | undefined
): { required: boolean; recommendation?: string } {
  const ratio = a_mm / t_mm;
  
  // Kryterium 1: stosunek rozpiętości do grubości
  if (ratio > 200) {
    return {
      required: true,
      recommendation: `Stosunek a/t = ${ratio.toFixed(0)} > 200. Zalecane żebra co 150-200mm`
    };
  }
  
  // Kryterium 2: dla pojemników z wodą
  if (H_mm !== undefined && H_mm >= 300 && t_mm < 4) {
    return {
      required: true,
      recommendation: `Wysokość słupa wody ${H_mm}mm z grubością ${t_mm}mm. Ryzyko wybrzuszeń - zalecane żebra co 150-200mm`
    };
  }
  
  // Kryterium 3: bardzo cienkie ścianki
  if (t_mm <= 2 && a_mm > 200) {
    return {
      required: true,
      recommendation: `Grubość ${t_mm}mm przy rozpiętości ${a_mm}mm. Zalecane żebra lub zwiększenie grubości`
    };
  }
  
  return { required: false };
}

/**
 * Oblicza parametry żeber wzmacniających
 */
export function calculateRibParameters(
  t_mm: number,
  a_mm: number,
  load_intensity: number
): {
  height_mm: number;
  thickness_mm: number;
  spacing_mm: number;
  count: number;
} {
  // Wysokość żebra: 10-12x grubość ścianki
  const height_mm = Math.min(Math.max(10 * t_mm, 20), 50); // min 20mm, max 50mm
  
  // Grubość żebra = grubość ścianki
  const thickness_mm = t_mm;
  
  // Rozstaw żeber zależy od obciążenia
  let spacing_mm: number;
  if (load_intensity > 10000) { // Duże obciążenie
    spacing_mm = 150;
  } else if (load_intensity > 5000) { // Średnie obciążenie
    spacing_mm = 200;
  } else { // Małe obciążenie
    spacing_mm = 250;
  }
  
  // Liczba żeber
  const count = Math.max(1, Math.floor(a_mm / spacing_mm));
  
  return {
    height_mm,
    thickness_mm,
    spacing_mm,
    count
  };
}

export { PlateCoefficients, CoeffTable };
